import { isNil } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, success } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post, put } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';

const LOADING = 'shipping/LOADING';
const LOADED = 'shipping/LOADED';
const RESET = 'shipping/RESET';
const NEW_ADDRESS = 'shipping/NEW_ADDRESS';
const EDIT_ADDRESS = 'shipping/EDIT_ADDRESS';
const REMOVE_ADDRESS = 'shipping/REMOVE_ADDRESS';

applyTranslations(require('./shipping.i18n.json'));

import type { ShippingState } from '../types/ShippingState.type';
import type {
  Action,
  Dispatch,
  ShippingAddress,
  ThunkAction
} from 'types';

const initialState: ShippingState = {
  isLoading: true,
  list: []
};

function shippingAddressesRemoved(data: ShippingAddress): Action {
  return {
    type: REMOVE_ADDRESS,
    payload: {
      data
    }
  };
}

function shippingAddressEdited(data: ShippingAddress): Action {
  return {
    type: EDIT_ADDRESS,
    payload: {
      data
    }
  };
}

function shippingAddressSaved(data: ShippingAddress): Action {
  return {
    type: NEW_ADDRESS,
    payload: {
      data
    }
  };
}

function shippingAddressesLoaded(data: Array<ShippingAddress>): Action {
  return {
    type: LOADED,
    payload: {
      data
    }
  };
}

function setLoadingStatus(status): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function deleteShippingAddress(organizationId: number, address: ShippingAddress): ThunkAction {
  const url = API.END_POINTS.API.getShippingAddresses(organizationId, address.id);

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    return destroy(url)
      .then(() => {
        dispatch(shippingAddressesRemoved(address));
      })
      .catch(() => {
        dispatch(setLoadingStatus(false));
      });
  };
}

export function saveShippingAddress(organizationId: number, address: ShippingAddress): ThunkAction {
  let url;
  let method;

  if (isNil(address.id)) {
    url = API.END_POINTS.API.getShippingAddresses(organizationId);
    method = post;
  } else {
    url = API.END_POINTS.API.getShippingAddresses(organizationId, address.id);
    method = put;
  }

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    return method(url, { data: address })
      .then(response => {
        if (isNil(address.id)) {
          dispatch(shippingAddressSaved(response.data));
        } else {
          dispatch(shippingAddressEdited(address));
        }

        dispatch(success(__t('shipping.success_message')));
      })
      .catch(() => {
        dispatch(setLoadingStatus(false));
      });
  };
}

export function fetchShipping(organizationId: number): ThunkAction {
  const url = API.END_POINTS.API.getShippingAddresses(organizationId);

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    return get(url)
      .then(response => {
        dispatch(shippingAddressesLoaded(response.data));
      })
      .catch(() => {
        dispatch(setLoadingStatus(false));
      });
  };
}

export function saveSubcampaignShipping(subcampaignId: number, address: Partial<ShippingAddress>): ThunkAction {
  const url = API.END_POINTS.SUBCAMPAIGNS.putSubcampaignShippingAddress(subcampaignId);

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));

    return put(url, { data: address })
      .then(() => {
        dispatch(setLoadingStatus(false));
      })
      .catch(() => {
        dispatch(setLoadingStatus(false));
      });
  };
}

export default createReducer<ShippingState, any>('shipping', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { data }) => ({
    ...state,
    list: [...data],
    isLoading: false
  }),
  [NEW_ADDRESS]: (state, { data }) => ({
    ...state,
    list: [...state.list, data],
    isLoading: false
  }),
  [EDIT_ADDRESS]: (state, { data }) => ({
    ...state,
    list: state.list.map(shipping => (shipping.id === data.id ? { ...data } : shipping)),
    isLoading: false
  }),
  [REMOVE_ADDRESS]: (state, { data }) => ({
    ...state,
    list: state.list.filter(shipping => shipping.id !== data.id),
    isLoading: false
  })
});
